import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Copy, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../../components/admin/ImageUploader";
import RemoveImageDialog from "../../components/admin/RemoveImageDialog";

export default function AdminMedia() {
    const [images, setImages] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [removeUrl, setRemoveUrl] = useState(null);

    const load = useCallback(async (cursor = null, append = false) => {
        if (append) setLoadingMore(true);
        else setLoading(true);
        try {
            const params = { max_results: 48 };
            if (cursor) params.cursor = cursor;
            const { data } = await api.get("/admin/cloudinary/images", { params });
            setImages((prev) => (append ? [...prev, ...data.images] : data.images));
            setNextCursor(data.nextCursor || null);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load images");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        document.title = "Media Library | Loomara Admin";
        load();
    }, [load]);

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => toast.success("URL copied to clipboard"));
    };

    return (
        <div data-testid="admin-media-page">
            <div>
                <p className="eyebrow">Cloud storage</p>
                <h1 className="font-serif text-4xl text-ink-900 mt-2">Media library</h1>
                <p className="text-sm text-ink-600 mt-2 max-w-xl">
                    All images uploaded to Cloudinary. Copy a URL to use on products, categories, or anywhere else.
                </p>
            </div>

            <div className="card-soft mt-8 p-6 max-w-xl">
                <ImageUploader
                    onUploadSuccess={() => {
                        toast.success("Uploaded — refreshing library");
                        load();
                    }}
                />
            </div>

            {loading && images.length === 0 ? (
                <div className="flex justify-center py-24 text-ink-500">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : images.length === 0 ? (
                <p className="text-sm text-ink-500 mt-12 text-center">No images yet. Upload one above.</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-8">
                    {images.map((img) => (
                        <article
                            key={img.publicId}
                            className="rounded-2xl border border-sage-300/30 bg-white overflow-hidden shadow-sm"
                            data-testid={`media-item-${img.publicId}`}
                        >
                            <div className="aspect-square bg-ivory-100">
                                <img src={img.url} alt="" className="h-full w-full object-cover" />
                            </div>
                            <div className="p-3 space-y-2">
                                <p className="text-[10px] text-ink-500 truncate" title={img.url}>
                                    {img.publicId}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => copyUrl(img.url)}
                                        className="flex-1 btn-secondary text-[10px] py-1.5 inline-flex items-center justify-center gap-1"
                                    >
                                        <Copy size={12} /> Copy URL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRemoveUrl(img.url)}
                                        className="p-1.5 rounded-lg text-blush-500 hover:bg-blush-50"
                                        aria-label="Delete from cloud"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {nextCursor && (
                <div className="flex justify-center mt-8">
                    <button
                        type="button"
                        onClick={() => load(nextCursor, true)}
                        disabled={loadingMore}
                        className="btn-secondary"
                    >
                        {loadingMore ? "Loading…" : "Load more"}
                    </button>
                </div>
            )}

            <RemoveImageDialog
                variant="library"
                open={!!removeUrl}
                onOpenChange={(open) => !open && setRemoveUrl(null)}
                imageUrl={removeUrl}
                onRemoved={async () => {
                    setRemoveUrl(null);
                    await load();
                }}
            />
        </div>
    );
}
